'use client'

import { Button, Spacer } from '@vinted/web-ui'
import { size } from 'lodash'

import { useState } from 'react'

import { useGetPendingSellerOfferMessage } from '@marketplace-web/offers/price-offers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { isDateInTheFuture } from '@marketplace-web/shared/utils'
import {
  OppositeUser,
  TransactionModel,
  AdditionalTransactionAction,
  ApplicableTransactionAction,
  TransactionSide,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useNavigateToCheckout } from '@marketplace-web/domain/checkout'
import { CheckoutOrderTypeMap, ExtraServiceOrderType } from '@marketplace-web/domain/checkout-data'

import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { MARK_AS_SOLD_URL } from 'constants/routes'

import { buyerBuyEvent, openOfferScreenEvent } from '_libs/common/event-tracker/events'

import { Reserve, Unreserve } from './Actions'
import { BuyButtonPosition } from '../../../constants/constants'

type Props = {
  itemId: number
  transaction?: TransactionModel | null
  oppositeUser?: OppositeUser | null
  actions: Array<ApplicableTransactionAction> | undefined
  openOfferModal: () => void
}

const HeroMessageActions = ({ transaction, actions, oppositeUser, openOfferModal }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.hero_message')

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)

  const pendingSellerOfferMessage = useGetPendingSellerOfferMessage()

  const { navigateToCheckout } = useNavigateToCheckout()

  const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

  const trackOfferModalOpening = ({ isBuyer }: { isBuyer: boolean }) => {
    if (!transaction) return

    track(
      openOfferScreenEvent({
        isBuyer,
        currentPrice: Number(transaction.offer.price.amount),
        currencyCode: transaction.offer.price.currencyCode,
        openedFrom: ClickableElement.TopScreenMakeOffer,
        transactionId: transaction.id,
      }),
    )
  }

  const handleOfferModalOpen = () => {
    openOfferModal()

    trackOfferModalOpening({ isBuyer: transaction?.userSide === TransactionSide.Buyer })
  }

  const getValidActions = (): Array<ApplicableTransactionAction> | null => {
    if (!actions) return null

    const validActions = [
      ApplicableTransactionAction.Offer,
      ApplicableTransactionAction.Buy,
      ApplicableTransactionAction.RequestOffer,
    ]

    return actions.filter(action => validActions.includes(action))
  }

  const handleBuyButtonClick = () => {
    setIsCheckoutLoading(true)

    if (!transaction?.id) return

    track(
      buyerBuyEvent({
        transactionId: transaction?.id,
        screen: Screen.MessageReply,
        sellerOfferId: pendingSellerOfferMessage?.id,
        buttonPosition: BuyButtonPosition.ConversationHeader,
      }),
    )

    navigateToCheckout(transaction.id, CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction])
  }

  const renderSellerReserveAction = () => {
    if (!actions?.includes(ApplicableTransactionAction.Reserve) || !transaction?.id) return null

    if (transaction?.order.reserved) {
      return <Unreserve transaction={transaction} />
    }

    return <Reserve transaction={transaction} oppositeUser={oppositeUser} />
  }

  const renderTransferAction = () => {
    if (!transaction?.id) return null

    if (actions?.includes(ApplicableTransactionAction.Transfer)) {
      if (transaction?.availableActions?.includes(AdditionalTransactionAction.MarkAsSold)) {
        return (
          <Button
            url={MARK_AS_SOLD_URL(transaction.id)}
            text={translate('transaction_actions.mark_as_sold')}
            size={Button.Size.Medium}
            styling={Button.Styling.Filled}
          />
        )
      }
    }

    return null
  }

  const renderMakeOfferRequestButton = () => {
    if (!actions?.includes(ApplicableTransactionAction.RequestOffer)) return null
    if (isSellerCountdownActive) return null
    if (!transaction?.id) return null

    return (
      <Button
        text={translate('actions.request_offer')}
        size={Button.Size.Medium}
        onClick={handleOfferModalOpen}
        testId="make-offer-request-button"
      />
    )
  }

  const renderMakeOfferButton = () => {
    if (!actions?.includes(ApplicableTransactionAction.Offer) || !transaction?.id) return null

    return (
      <Button
        text={translate('actions.offer')}
        size={Button.Size.Medium}
        styling={Button.Styling.Filled}
        onClick={handleOfferModalOpen}
        testId="make-offer-button"
      />
    )
  }

  const renderNonPaymentsActions = () => {
    const showSpacer =
      actions?.includes(ApplicableTransactionAction.Transfer) &&
      transaction?.availableActions?.includes(AdditionalTransactionAction.MarkAsSold)

    if (
      !!transaction &&
      !(
        actions?.includes(ApplicableTransactionAction.Reserve) &&
        actions?.includes(ApplicableTransactionAction.Transfer)
      )
    ) {
      return null
    }

    return (
      <div className="u-flexbox">
        {renderSellerReserveAction()}
        {showSpacer && <Spacer orientation={Spacer.Orientation.Vertical} />}
        {renderTransferAction()}
      </div>
    )
  }

  const renderPaymentsActions = () => {
    if (!actions) return null

    const showBuyButton =
      actions.includes(ApplicableTransactionAction.Buy) && !isSellerCountdownActive

    return (
      <div className="u-flexbox">
        {renderMakeOfferRequestButton()}

        {size(getValidActions()) > 1 && <Spacer orientation={Spacer.Orientation.Vertical} />}

        {showBuyButton && transaction?.id && (
          <Button
            text={translate('actions.buy')}
            isLoading={isCheckoutLoading}
            disabled={isCheckoutLoading}
            size={Button.Size.Medium}
            styling={Button.Styling.Filled}
            onClick={handleBuyButtonClick}
            testId="hero-message-buy-now-button"
          />
        )}

        {renderMakeOfferButton()}
      </div>
    )
  }

  const showPaymentsButtons =
    actions?.includes(ApplicableTransactionAction.Buy) ||
    actions?.includes(ApplicableTransactionAction.Offer) ||
    actions?.includes(ApplicableTransactionAction.RequestOffer)

  return showPaymentsButtons ? renderPaymentsActions() : renderNonPaymentsActions()
}

export default HeroMessageActions
